import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Conversation, SlackUser } from '@assembly/slack'
import { create } from 'zustand'
import { usePlatform } from '../platform'
import { useWorkspace } from '../data/workspace'
import { useConversations, useUsers } from '../data/hooks'
import { keys } from '../data/keys'
import { useUi } from './ui'
import { emptyLayout, type SidebarLayout } from '../sidebar/layout'
import { SidebarSync, type SyncStatus } from '../sidebar/sync'
import { countUnread, Pacer, scanOrder } from '../sidebar/unreads'
import { relayQuiet } from '../data/realtime'

interface SidebarState {
  layout: SidebarLayout
  status: SyncStatus
  /** Applies a layout edit (see sidebar/layout.ts); persists locally and syncs. */
  edit(fn: (layout: SidebarLayout) => SidebarLayout): void
}

let sync: SidebarSync | null = null

export const useSidebar = create<SidebarState>(() => ({
  layout: emptyLayout(),
  status: 'local',
  edit: (fn) => sync?.edit(fn),
}))

/** Loads and syncs the sidebar layout for the active workspace. */
export function useSidebarSync() {
  const platform = usePlatform()
  const { account } = useWorkspace()

  useEffect(() => {
    const instance = new SidebarSync({
      storage: platform.storage,
      prefsUrl: platform.prefsUrl ?? null,
      token: account.token,
      storageKey: `assembly.sidebar.${account.id}`,
      onChange: (layout, status) => useSidebar.setState({ layout, status }),
    })
    sync = instance
    useSidebar.setState({ layout: emptyLayout(), status: instance.status })
    instance.start()

    // Pick up changes made on other devices when the window comes back.
    let last = Date.now()
    const onFocus = () => {
      if (Date.now() - last < 30_000) return
      last = Date.now()
      instance.refresh()
    }
    window.addEventListener('focus', onFocus)
    const interval = setInterval(() => document.visibilityState === 'visible' && instance.refresh(), 5 * 60_000)
    return () => {
      instance.close()
      if (sync === instance) sync = null
      window.removeEventListener('focus', onFocus)
      clearInterval(interval)
    }
  }, [platform, account.id, account.token])
}

/**
 * Works out unread state for every conversation in the background, most active first,
 * pacing calls to stay inside Slack's limits. Repeats every 10 minutes when real-time
 * events aren't available (live events keep it current otherwise).
 */
export function useUnreadScan() {
  const { account, client } = useWorkspace()
  const qc = useQueryClient()
  const { data: convs } = useConversations()
  const realtime = useUi((s) => s.realtime)
  const realtimeRef = useRef(realtime)
  realtimeRef.current = realtime
  const ready = !!convs

  useEffect(() => {
    if (!ready) return
    let cancelled = false
    // ~40 calls/minute per method: inside Tier 3, leaving room for everything else.
    const infoPacer = new Pacer(1500)
    const historyPacer = new Pacer(1500)

    const scan = async () => {
      const started = Date.now()
      const list = qc.getQueryData<Conversation[]>(keys.conversations(account.id)) ?? []
      const queue = scanOrder(list, { current: useUi.getState().channelId })
      const worker = async () => {
        while (!cancelled && queue.length) {
          const c = queue.shift()!
          try {
            await infoPacer.wait()
            if (cancelled) return
            const info = await client.conversations.info(c.id)
            if (c.is_im) {
              const unread = info.unread_count_display ?? 0
              useUi.getState().setScannedUnread(c.id, unread, unread, info.latest?.ts, started)
              continue
            }
            if (!info.last_read) continue
            await historyPacer.wait()
            if (cancelled) return
            const res = await client.conversations.history(c.id, { oldest: info.last_read, limit: 20 })
            const result = countUnread(res.messages, info.last_read, account.userId, !!c.is_mpim, res.has_more)
            // Nothing newer than the read marker means the last message is the one you read.
            useUi.getState().setScannedUnread(c.id, result.unread, result.mentions, result.latest ?? (Number(info.last_read) > 0 ? info.last_read : undefined), started)
          } catch {
            /* not a member any more, rate limited past retries, offline: skip */
          }
        }
      }
      await Promise.all([worker(), worker(), worker()])
    }

    let running = scan()
    const interval = setInterval(() => {
      if (realtimeRef.current !== 'live' || relayQuiet(10 * 60_000)) running = running.then(scan)
    }, 10 * 60_000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [ready, account.id, account.userId, client, qc])
}

const PEOPLE_TTL = 7 * 24 * 60 * 60_000

/**
 * People you have DMs with who aren't in users.list (other organizations, via Slack
 * Connect): looks each one up with users.info, merges them into the users cache so
 * names render everywhere, and keeps them on this device so they show instantly next time.
 */
export function useResolveUsers() {
  const platform = usePlatform()
  const qc = useQueryClient()
  const { account, client } = useWorkspace()
  const { data: convs } = useConversations()
  const { data: users } = useUsers()
  const storageKey = `assembly.people.${account.id}`
  const inflight = useRef(new Set<string>())
  const cache = useRef<Record<string, { user: SlackUser; at: number }> | null>(null)

  const merge = (list: SlackUser[]) => {
    if (!list.length) return
    qc.setQueryData<Map<string, SlackUser>>(keys.users(account.id), (prev) => {
      if (!prev) return prev
      const next = new Map(prev)
      for (const u of list) if (!next.has(u.id) || next.get(u.id)?.team_id !== account.teamId) next.set(u.id, u)
      return next
    })
  }

  // Load what we looked up before as soon as the directory is in.
  useEffect(() => {
    if (!users || cache.current) return
    cache.current = {}
    platform.storage
      .get(storageKey)
      .then((raw) => {
        cache.current = raw ? JSON.parse(raw) : {}
        merge(Object.values(cache.current!).map((e) => e.user))
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, storageKey])

  useEffect(() => {
    if (!convs || !users || !cache.current) return
    const now = Date.now()
    const current = qc.getQueryData<Map<string, SlackUser>>(keys.users(account.id)) ?? users
    const wanted = [...new Set(convs.filter((c) => c.is_im && c.user && !c.is_user_deleted).map((c) => c.user!))]
    const todo = wanted.filter((id) => {
      if (inflight.current.has(id)) return false
      const cached = cache.current![id]
      if (cached) return now - cached.at > PEOPLE_TTL // refresh weekly
      return !current.has(id)
    })
    if (!todo.length) return
    let cancelled = false
    const pacer = new Pacer(600) // users.info is Tier 4 (~100/min)
    const found: SlackUser[] = []
    let saveTimer: ReturnType<typeof setTimeout> | undefined
    const flush = () => {
      merge(found.splice(0))
      clearTimeout(saveTimer)
      saveTimer = setTimeout(() => platform.storage.set(storageKey, JSON.stringify(cache.current)).catch(() => {}), 1000)
    }
    const queue = [...todo]
    todo.forEach((id) => inflight.current.add(id))
    const worker = async () => {
      while (!cancelled && queue.length) {
        const id = queue.shift()!
        try {
          await pacer.wait()
          if (cancelled) return
          const user = await client.users.info(id)
          cache.current![id] = { user, at: Date.now() }
          found.push(user)
          if (found.length >= 5) flush()
        } catch {
          /* unknown or inaccessible user: leave the id */
        }
      }
    }
    Promise.all([worker(), worker(), worker()]).then(() => !cancelled && flush())
    return () => {
      cancelled = true
      todo.forEach((id) => inflight.current.delete(id))
      if (found.length) flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convs, users === undefined, account.id, client])
}

/** Keeps last-message times on this device so the 30-day DM list is right from the first frame. */
export function usePersistLastMessages() {
  const platform = usePlatform()
  const { account } = useWorkspace()
  const key = `assembly.lastmsg.${account.id}`
  const lastMessage = useUi((s) => s.lastMessage)
  const loaded = useRef<string | null>(null)

  useEffect(() => {
    loaded.current = null
    platform.storage
      .get(key)
      .then((raw) => {
        if (raw) useUi.getState().setLastMessages(JSON.parse(raw))
      })
      .catch(() => {})
      .finally(() => (loaded.current = key))
  }, [platform, key])

  useEffect(() => {
    if (loaded.current !== key) return
    const t = setTimeout(() => platform.storage.set(key, JSON.stringify(lastMessage)).catch(() => {}), 2000)
    return () => clearTimeout(t)
  }, [lastMessage, key, platform])
}
